import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ImageUpload } from "@/components/ImageUpload";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Images } from "lucide-react";
import { toast } from "sonner";

const ALBUMS = ["General", "Yoga Camp", "Kutch Welfare", "Ashram Darshan", "Festivals"];
const empty = { image_path: null, title: "", caption: "", album: "General", published: false };

export default function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/gallery").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openAdd = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ image_path: g.image_path || null, title: g.title, caption: g.caption || "", album: g.album || "General", published: g.published }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image_path) { toast.error("Please upload an image"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/gallery/${editing.id}`, form);
      else await api.post("/admin/gallery", form);
      toast.success(editing ? "Image updated" : "Image added");
      setDialogOpen(false); load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const togglePublish = async (g) => {
    try { await api.patch(`/admin/gallery/${g.id}/publish`, { published: !g.published }); toast.success(!g.published ? "Published" : "Unpublished"); load(); }
    catch { toast.error("Failed to update"); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/admin/gallery/${deleteId}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
    setDeleteId(null);
  };

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">Gallery</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">Photo Gallery</h1>
        </div>
        <Button onClick={openAdd} className="bg-[#194324] hover:bg-[#2D623A] text-white rounded-full" data-testid="admin-add-gallery-button">
          <Plus className="h-4 w-4 mr-2" /> Add Image
        </Button>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2DAD0] p-12 text-center text-[#5C7264]"><Images className="h-10 w-10 mx-auto mb-3 text-[#CBD2CC]" />No gallery images yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((g) => (
            <div key={g.id} className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden" data-testid={`gallery-card-${g.id}`}>
              <div className="aspect-square bg-[#F2EEE5] relative">
                {g.image_path ? <img src={fileUrl(g.image_path)} alt={g.title} className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><Images className="h-8 w-8 text-[#CBD2CC]" /></div>}
                <Badge className={`absolute top-2 left-2 border-0 text-[10px] ${g.published ? "bg-[#194324] text-white" : "bg-[#F9F3E3] text-[#A8811A]"}`}>{g.published ? "Published" : "Draft"}</Badge>
              </div>
              <div className="p-3">
                <div className="font-medium text-[#194324] text-sm truncate">{g.title}</div>
                <div className="text-xs text-[#B38728] font-mono">{g.album}</div>
                <div className="mt-2 flex items-center justify-between border-t border-[#F2EEE5] pt-2">
                  <Switch checked={g.published} onCheckedChange={() => togglePublish(g)} data-testid={`gallery-publish-toggle-switch-${g.id}`} />
                  <div>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(g)} data-testid={`gallery-edit-${g.id}`}><Pencil className="h-4 w-4 text-[#2D623A]" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteId(g.id)} data-testid={`gallery-delete-${g.id}`}><Trash2 className="h-4 w-4 text-[#D9531E]" /></Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{editing ? "Edit Image" : "Add Image"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Image</Label><ImageUpload value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} testid="gallery-form-image" /></div>
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="gallery-form-title" /></div>
            <div className="space-y-2"><Label>Caption</Label><Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="gallery-form-caption" /></div>
            <div className="space-y-2">
              <Label>Album</Label>
              <select value={form.album} onChange={(e) => setForm({ ...form, album: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="gallery-form-album">
                {ALBUMS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3 bg-[#F9F3E3] rounded-lg px-4 py-3">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="gallery-form-publish" />
              <span className="text-sm text-[#194324]">Publish to public site</span>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-[#194324] hover:bg-[#2D623A] text-white" data-testid="gallery-form-submit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update" : "Add Image"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete image?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-[#D9531E] hover:bg-[#b8441a]" data-testid="confirm-delete-button">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
